import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { Segment } from '@feathr/blackbox';
import { FieldCollection } from '@feathr/blackbox';
import { useExportModal } from '@feathr/extender/hooks/useExportModal';
import { useStore } from '@feathr/extender/state';

import type { IPersonFieldOption } from './GroupExportModal.utils';
import {
  DEFAULT_PERSON_FIELD_IDS,
  getPersonFieldOptions,
  resolveExportNameTemplate,
} from './GroupExportModal.utils';

export type TGroupExportTab = 'person' | 'emailActivity' | 'otherActivities';

export interface IUseGroupExportModalReturn {
  activeTab: TGroupExportTab;
  setActiveTab: (tab: TGroupExportTab) => void;
  exportName: string;
  /** Tracks whether the Export name input has been focused; gates error display. */
  exportNameHadFocus: boolean;
  email: string;
  /** Tracks whether the Email input has been focused; gates error display. */
  emailHadFocus: boolean;
  /** True while the Person custom-fields collection is loading. */
  isCustomFieldsPending: boolean;
  personFieldOptions: IPersonFieldOption[];
  selectedFieldOptions: IPersonFieldOption[];
  /** True while a submit request is in flight. */
  isLoading: boolean;
  /** Combined: base form validation + at-least-one-field-selected. Does not consider tab. */
  isSubmitDisabled: boolean;
  exportNameError: string[] | undefined;
  emailError: string[] | undefined;
  fieldsError: string[] | undefined;
  handleChangeExportName: (newName?: string) => void;
  handleExportNameBlur: () => void;
  handleChangeEmail: (newEmail?: string) => void;
  handleEmailBlur: () => void;
  handleSelectFields: (values: IPersonFieldOption[]) => void;
  handleSubmitPerson: () => Promise<void>;
  handleClose: () => void;
}

/** Slice consumed by `PersonTab` — fields and handlers the tab itself owns. */
export type IPersonTabState = Pick<
  IUseGroupExportModalReturn,
  | 'exportName'
  | 'exportNameHadFocus'
  | 'email'
  | 'emailHadFocus'
  | 'isCustomFieldsPending'
  | 'personFieldOptions'
  | 'selectedFieldOptions'
  | 'exportNameError'
  | 'emailError'
  | 'fieldsError'
  | 'handleChangeExportName'
  | 'handleExportNameBlur'
  | 'handleChangeEmail'
  | 'handleEmailBlur'
  | 'handleSelectFields'
>;

/**
 * Owns state for `GroupExportModal`: active tab, Person tab field selection,
 * and the shared `useExportModal` slice (export name, email).
 */
export function useGroupExportModal({
  segment,
  onClose,
}: {
  segment: Segment;
  onClose: () => void;
}): IUseGroupExportModalReturn {
  const { t } = useTranslation();
  const { CustomFields } = useStore();

  // Reactive read of `segment.get('name')` so MobX re-renders once the segment
  // finishes loading. The modal mounts only when opened (parent gates), so the
  // hook initializes with the latest name without a re-seed effect.
  const resolvedExportName = resolveExportNameTemplate(segment.get('name', ''));

  const {
    email,
    emailHadFocus,
    exportName,
    exportNameHadFocus,
    isLoading,
    isSubmitDisabled: isBaseSubmitDisabled,
    validationErrors,
    handleChangeEmail,
    handleEmailBlur,
    handleChangeExportName,
    handleExportNameBlur,
    handleExport,
    resetFormState,
  } = useExportModal(t, { initialExportName: resolvedExportName });

  const [activeTab, setActiveTab] = useState<TGroupExportTab>('person');
  const [selectedFieldIds, setSelectedFieldIds] = useState<string[]>([...DEFAULT_PERSON_FIELD_IDS]);

  const customFieldsResult = CustomFields.list({
    filters: { collection: FieldCollection.Person },
  });

  const personFieldOptions = useMemo(
    () => (customFieldsResult.isPending ? [] : getPersonFieldOptions(customFieldsResult.models)),
    [customFieldsResult.isPending, customFieldsResult.models],
  );

  const selectedFieldOptions = useMemo(
    () => personFieldOptions.filter((option) => selectedFieldIds.includes(option.id)),
    [personFieldOptions, selectedFieldIds],
  );

  const fieldsError = useMemo<string[] | undefined>(
    () => (selectedFieldIds.length === 0 ? [t('Choose at least one field to export.')] : undefined),
    [selectedFieldIds.length, t],
  );

  const isSubmitDisabled = isBaseSubmitDisabled || fieldsError !== undefined;

  const handleSelectFields = useCallback((values: IPersonFieldOption[]): void => {
    setSelectedFieldIds(values.map((v) => v.id));
  }, []);

  const handleClose = useCallback((): void => {
    resetFormState();
    setSelectedFieldIds([...DEFAULT_PERSON_FIELD_IDS]);
    setActiveTab('person');
    onClose();
  }, [onClose, resetFormState]);

  const handleSubmitPerson = useCallback(async (): Promise<void> => {
    const fields = selectedFieldOptions.map((opt) => opt.id);
    const headers = selectedFieldOptions.map((opt) => opt.name);

    await handleExport(
      () => segment.export(fields, headers, email, 'persons', exportName),
      handleClose,
      t,
    );
  }, [email, exportName, handleClose, handleExport, segment, selectedFieldOptions, t]);

  return {
    activeTab,
    setActiveTab,
    exportName,
    exportNameHadFocus,
    email,
    emailHadFocus,
    isCustomFieldsPending: customFieldsResult.isPending,
    personFieldOptions,
    selectedFieldOptions,
    isLoading,
    isSubmitDisabled,
    exportNameError: validationErrors?.exportName,
    emailError: validationErrors?.email,
    fieldsError,
    handleChangeExportName,
    handleExportNameBlur,
    handleChangeEmail,
    handleEmailBlur,
    handleSelectFields,
    handleSubmitPerson,
    handleClose,
  };
}
